<app-popup
  [heading]="popupHeading"
  [confirmText]="confirmButtonText"
  [cancelText]="'Cancel'"
  [popupPosition]="popupPosition"
  [showCloseIcon]="true"
  [isConfirmDisabled]="isConfirmDisabled"
  (confirmClicked)="onContinue()"
  (cancelClicked)="onCancel()"
>
  <div class="unassign-popup-content">
    @if (needsStatusSelection) {
      <p class="popup-subtitle">
        You are unassigning <strong class="lead-name">{{ leadName }}</strong
        >. Please select a reason below:
      </p>
    } @else {
      <p class="popup-subtitle">
        You are unassigning <strong class="lead-name">{{ leadName }}</strong
        >.
      </p>
    }

    @if (currentStatusLabel) {
      <div class="current-status-banner">
        <span class="status-pulse-dot"></span>
        <span class="status-prefix">Current Status:</span>
        <span class="status-value">{{ currentStatusLabel }}</span>
      </div>
    }

    @if (needsStatusSelection) {
      <!-- Status Reason Selection View -->
      <div
        class="options-list"
        role="radiogroup"
        aria-label="Unassign reason status"
      >
        @for (option of options; track option.value) {
          <div
            class="option-item"
            [class.selected]="selectedStatus === option.value"
            [class]="'accent-' + option.colorClass"
            (click)="selectOption(option)"
            role="radio"
            [attr.aria-checked]="selectedStatus === option.value"
            tabindex="0"
            (keydown.space)="$event.preventDefault(); selectOption(option)"
            (keydown.enter)="$event.preventDefault(); selectOption(option)"
          >
            <!-- Option Icon -->
            <div
              class="option-icon"
              [class.selected]="selectedStatus === option.value"
            >
              @switch (option.icon) {
                @case ("pause") {
                  <svg
                    viewBox="0 0 20 20"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                  >
                    <circle cx="10" cy="10" r="8.5" />
                    <line
                      x1="8.5"
                      y1="7"
                      x2="8.5"
                      y2="13"
                      stroke-linecap="round"
                    />
                    <line
                      x1="11.5"
                      y1="7"
                      x2="11.5"
                      y2="13"
                      stroke-linecap="round"
                    />
                  </svg>
                }
                @case ("minus") {
                  <svg
                    viewBox="0 0 20 20"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                  >
                    <circle cx="10" cy="10" r="8.5" />
                    <line
                      x1="6.5"
                      y1="10"
                      x2="13.5"
                      y2="10"
                      stroke-linecap="round"
                    />
                  </svg>
                }
                @case ("info") {
                  <svg
                    viewBox="0 0 20 20"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                  >
                    <circle cx="10" cy="10" r="8.5" />
                    <line
                      x1="10"
                      y1="6.5"
                      x2="10"
                      y2="10.5"
                      stroke-linecap="round"
                    />
                    <circle
                      cx="10"
                      cy="13.5"
                      r="0.9"
                      fill="currentColor"
                      stroke="none"
                    />
                  </svg>
                }
                @case ("close") {
                  <svg
                    viewBox="0 0 20 20"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                  >
                    <circle cx="10" cy="10" r="8.5" />
                    <line
                      x1="7.2"
                      y1="7.2"
                      x2="12.8"
                      y2="12.8"
                      stroke-linecap="round"
                    />
                    <line
                      x1="12.8"
                      y1="7.2"
                      x2="7.2"
                      y2="12.8"
                      stroke-linecap="round"
                    />
                  </svg>
                }
              }
            </div>

            <!-- Text Content -->
            <div class="option-text">
              <span class="option-label">{{ option.label }}</span>
              <span class="option-desc">{{ option.description }}</span>
            </div>

            <!-- Selection Checkmark on the right -->
            <div
              class="selection-check"
              [class.visible]="selectedStatus === option.value"
            >
              <svg
                viewBox="0 0 20 20"
                width="14"
                height="14"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        }
      </div>

      <!-- Engaging Dynamic Preview Banner -->
      <div class="status-preview-container">
        @if (selectedOption) {
          <div class="status-preview-box">
            <div class="preview-header">
              <svg
                viewBox="0 0 20 20"
                width="16"
                height="16"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span class="status-preview-text">
                Status will change to
                <strong>{{ selectedOption.label }}</strong>
              </span>
            </div>
            <p class="preview-note">{{ selectedOption.reassuranceNote }}</p>
          </div>
        } @else {
          <p class="select-prompt-text">Select an option above to proceed</p>
        }
      </div>
    } @else {
      <!-- Confirmation Mode (already in one of the given statuses or no status) -->
      <div class="confirm-body">
        <div class="info-card">
          <svg viewBox="0 0 20 20" width="18" height="18" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clip-rule="evenodd"
            />
          </svg>
          <span
            >You can come back, reassign this lead, and change its status at any
            time.</span
          >
        </div>
      </div>
    }

    <!-- Status Notes Section (Always visible) -->
    <div class="status-notes-section">
      <div class="notes-header-bar">
        <div class="notes-header-left">
          <div class="notes-header-icon">
            <svg viewBox="0 0 20 20" width="13" height="13" fill="currentColor">
              <path
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm1 3a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm0 3a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm0 3a1 1 0 011-1h5a1 1 0 110 2H6a1 1 0 01-1-1z"
              />
            </svg>
          </div>
          <span class="notes-title">Status Notes</span>
        </div>
      </div>

      <div class="notes-body">
        <textarea
          class="note-textarea"
          [(ngModel)]="currentStatusNotes"
          rows="3"
          placeholder="Add details, next steps, or context for this lead..."
          [disabled]="isSavingNote || isSubmitting"
        ></textarea>
      </div>
    </div>
  </div>
</app-popup>
